import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import TabBar from '../components/TabBar';
import HomePage from './HomePage';
import CategoryPage from './CategoryPage';
import MapPage from './MapPage';
import MemoriesPage from './MemoriesPage';
import SettingsPage from './SettingsPage';
import AddRecordPage from './AddRecordPage';
import CategoryManagePage from './CategoryManagePage';
import ProfileManagePage from './ProfileManagePage';
import ExportPage from './ExportPage';
import StatsPage from './StatsPage';
import OnboardingPage from './OnboardingPage';
import LockScreen, { isBioInProgress } from '../components/LockScreen';
import './MainPage.css';

// 샘플 데이터 삽입 (첫 가입 시 1회)
async function insertSampleRecords(userId, profileId, setProfilesFn) {
  // === 1) '나' 프로필 샘플 기록 ===
  const mySamples = [
    {
      category_name: '거주지', category_emoji: '🏠',
      title: '롯데시그니엘', start_date: '2025-01-01', end_date: null, is_ongoing: true,
      location_name: '서울 롯데시그니엘', latitude: 37.5126, longitude: 127.1025,
      memo: '전세만기 2년뒤 (샘플 데이터 입니다.)', photos: ['/samples/apt.png'],
    },
    {
      category_name: '여행', category_emoji: '✈️',
      title: '부산 광안리', start_date: '2026-01-01', end_date: '2026-01-01', is_ongoing: false,
      location_name: '광안리해수욕장', latitude: 35.1531, longitude: 129.1186,
      memo: '샘플 데이터 입니다.', photos: ['/samples/beach.png'],
    },
    {
      category_name: '자동차', category_emoji: '🚗',
      title: 'G80', start_date: '2026-01-01', end_date: null, is_ongoing: true,
      location_name: '수원역', latitude: 37.2660, longitude: 127.0001,
      memo: '샘플 데이터 입니다.', photos: ['/samples/g80.png'],
    },
    {
      category_name: '학교', category_emoji: '🏫',
      title: '경희대', start_date: '2024-01-01', end_date: null, is_ongoing: true,
      location_name: '경희대 서울캠퍼스', latitude: 37.5974, longitude: 127.0512,
      memo: '샘플 데이터 입니다.', photos: ['/samples/K.png'],
    },
  ];

  try {
    const myRows = mySamples.map((s) => ({ ...s, user_id: userId, profile_id: profileId }));
    const { data: myInserted, error: myErr } = await supabase
      .from('records').insert(myRows).select('id');

    if (myErr || !myInserted || myInserted.length === 0) {
      console.error('[lifelog] 샘플 레코드 삽입 실패:', myErr?.message);
      return false;
    }

    const myShares = myInserted.map((r) => ({ record_id: r.id, profile_id: profileId }));
    await supabase.from('record_shares').insert(myShares);

    // === 2) '자녀1' 프로필 생성 + 샘플 기록 ===
    const { data: childProfile, error: childErr } = await supabase
      .from('profiles')
      .insert({ user_id: userId, name: '자녀1', emoji: '👧', is_default: false })
      .select()
      .single();

    if (!childErr && childProfile) {
      if (setProfilesFn) {
        setProfilesFn((prev) => [...prev, childProfile]);
      }

      const childSamples = [
        {
          category_name: '탄생', category_emoji: '👶',
          title: '탄생', start_date: '2018-08-30', end_date: '2018-08-30', is_ongoing: false,
          location_name: '산본제일병원', latitude: 37.3945, longitude: 126.9275,
          memo: '우리 아이가 태어난 날 (샘플 데이터 입니다.)', photos: [],
        },
        {
          category_name: '성장', category_emoji: '🚶',
          title: '첫 걸음마', start_date: '2019-06-25', end_date: '2019-06-25', is_ongoing: false,
          location_name: null, latitude: null, longitude: null,
          memo: '드디어 첫 걸음마를 뗐어요! (샘플 데이터 입니다.)', photos: [],
        },
        {
          category_name: '성장', category_emoji: '🗣️',
          title: '첫 엄마라고 말한 날', start_date: '2019-07-15', end_date: '2019-07-15', is_ongoing: false,
          location_name: null, latitude: null, longitude: null,
          memo: '처음으로 "엄마"라고 불러줬어요! (샘플 데이터 입니다.)', photos: [],
        },
        {
          category_name: '학교', category_emoji: '🏫',
          title: '첫 어린이집', start_date: '2020-01-20', end_date: null, is_ongoing: false,
          location_name: '호매실 해나라 어린이집', latitude: 37.2714, longitude: 126.9632,
          memo: '첫 어린이집 등원! (샘플 데이터 입니다.)', photos: [],
        },
      ];

      const childRows = childSamples.map((s) => ({ ...s, user_id: userId, profile_id: childProfile.id }));
      const { data: childInserted, error: childRecErr } = await supabase
        .from('records').insert(childRows).select('id');

      if (!childRecErr && childInserted && childInserted.length > 0) {
        // 자녀1 프로필에만 연결 (나 프로필에는 연결하지 않음)
        const childShares = childInserted.map((r) => ({ record_id: r.id, profile_id: childProfile.id }));
        await supabase.from('record_shares').insert(childShares);
      }
    }

    return true;
  } catch (err) {
    console.error('[lifelog] 샘플 삽입 예외:', err);
    return false;
  }
}

// initProfiles 동시 실행 방지
let initProfilesPromise = null;

export default function MainPage({ user }) {
  const [activeTab, setActiveTab] = useState(() => {
    const validTabs = ['home', 'category', 'map', 'memories', 'settings'];
    // ?tab=xxx 또는 #tab=xxx 둘 다 지원 (TWA 호환)
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    const tab = params.get('tab') || hashParams.get('tab');
    return validTabs.includes(tab) ? tab : 'home';
  });
  const [screen, setScreen] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    const action = params.get('action') || hashParams.get('action');
    return action === 'addRecord' ? 'addRecord' : null;
  });
  const [screenData, setScreenData] = useState(null);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem(`lifelog_onboarding_done_${user.id}`)
  );
  const [isNewUser, setIsNewUser] = useState(false); // 신규 가입 여부
  const [isLocked, setIsLocked] = useState(
    () => localStorage.getItem('lifelog_lock_enabled') === 'true'
  );

  // 데이터 보정 (중복 제거, record_shares 누락 수정)
  const runMaintenance = useCallback(async (profileId) => {
    try {
      // 프로필 중복 제거
      const { data: allProfs } = await supabase
        .from('profiles')
        .select('id, name')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });
      if (allProfs) {
        const seen = new Set();
        const dupeIds = [];
        for (const p of allProfs) {
          if (seen.has(p.name)) dupeIds.push(p.id);
          else seen.add(p.name);
        }
        if (dupeIds.length > 0) {
          await supabase.from('record_shares').delete().in('profile_id', dupeIds);
          await supabase.from('profiles').delete().in('id', dupeIds);
          const { data: full } = await supabase
            .from('profiles').select('*').eq('user_id', user.id)
            .order('created_at', { ascending: true });
          if (full) setProfiles(full);
        }
      }

      // profile_id null인 기록 보정
      await supabase.from('records')
        .update({ profile_id: profileId })
        .eq('user_id', user.id)
        .is('profile_id', null);

      // record_shares 누락 보정 (핵심: HomePage가 inner join으로 조회)
      const { data: records } = await supabase
        .from('records').select('id').eq('user_id', user.id);
      if (records && records.length > 0) {
        const ids = records.map((r) => r.id);
        const { data: shares } = await supabase
          .from('record_shares').select('record_id')
          .eq('profile_id', profileId).in('record_id', ids);
        const sharedSet = new Set(shares?.map((s) => s.record_id) || []);
        const missing = ids.filter((id) => !sharedSet.has(id));
        if (missing.length > 0) {
          await supabase.from('record_shares').insert(
            missing.map((id) => ({ record_id: id, profile_id: profileId }))
          );
          setRefreshSignal((s) => s + 1);
        }
      }
    } catch (err) {
      console.error('[lifelog] maintenance error:', err);
    }
  }, [user.id]);

  const initProfiles = useCallback(async () => {
    if (initProfilesPromise) return initProfilesPromise;

    initProfilesPromise = (async () => {
    try {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });

    let profile;

    if (data && data.length > 0) {
      const savedId = localStorage.getItem(`lifelog_profile_${user.id}`);
      const found = data.find((p) => p.id === savedId);
      profile = found || data[0];
      setProfiles(data);

      // 샘플 데이터 삽입이 이전에 실패했으면 재시도
      if (!localStorage.getItem(`lifelog_samples_done_${user.id}`)) {
        const { count } = await supabase
          .from('records').select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        if (count === 0) {
          const ok = await insertSampleRecords(user.id, profile.id, setProfiles);
          if (ok) localStorage.setItem(`lifelog_samples_done_${user.id}`, '1');
          setRefreshSignal((s) => s + 1);
        } else {
          localStorage.setItem(`lifelog_samples_done_${user.id}`, '1');
        }
      }
    } else if (data) {
      const { data: newProfile, error } = await supabase
        .from('profiles')
        .insert({ user_id: user.id, name: '나', emoji: '👤', is_default: true })
        .select()
        .single();
      if (!error && newProfile) {
        profile = newProfile;
        setProfiles([newProfile]);
        // 첫 가입: 온보딩 플래그 + 샘플 데이터 삽입 (텔레그램 알림은 DB 트리거가 담당)
        setIsNewUser(true);
        const ok = await insertSampleRecords(user.id, newProfile.id, setProfiles);
        if (ok) localStorage.setItem(`lifelog_samples_done_${user.id}`, '1');
        setRefreshSignal((s) => s + 1);
      } else if (error) {
        console.error('[lifelog] 프로필 생성 실패:', error.message);
      }
    }

    if (!profile) return;

    // 즉시 프로필 설정 → HomePage 빠르게 표시
    setCurrentProfile((prev) => {
      if (prev && (data || []).find((p) => p.id === prev.id)) return prev;
      return profile;
    });

    // 나머지 보정은 백그라운드에서 실행
    runMaintenance(profile.id);
    } finally {
      initProfilesPromise = null;
    }
    })();

    return initProfilesPromise;
  }, [user, runMaintenance]);

  useEffect(() => {
    initProfiles();
  }, [user.id, initProfiles]);

  // 앱이 백그라운드에서 돌아오면 잠금
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible'
          && localStorage.getItem('lifelog_lock_enabled') === 'true'
          && !isBioInProgress()) {
        setIsLocked(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // URL 파라미터 정리 (위젯에서 ?tab=xxx, ?action=xxx 로 진입 후)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    if (params.has('tab') || params.has('action') || hash.includes('tab=') || hash.includes('action=')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // 기존 사용자가 다른 브라우저(인앱 브라우저 등)에서 접속 시 온보딩 자동 스킵
  // 신규 사용자(isNewUser=true)는 온보딩을 정상 표시
  useEffect(() => {
    if (showOnboarding && currentProfile && !isNewUser) {
      localStorage.setItem(`lifelog_onboarding_done_${user.id}`, 'true');
      setShowOnboarding(false);
    }
  }, [showOnboarding, currentProfile, isNewUser, user.id]);

  const handleProfileChange = (profile) => {
    setCurrentProfile(profile);
    localStorage.setItem(`lifelog_profile_${user.id}`, profile.id);
  };

  const closeScreen = (shouldRefresh) => {
    setScreen(null);
    setScreenData(null);
    if (shouldRefresh) setRefreshSignal((s) => s + 1);
  };

  // 하위 화면 진입 시 history에 쌓아서 기기 뒤로가기 → 목록 복귀 (앱 종료 방지)
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const pendingRefreshRef = useRef(null);

  useEffect(() => {
    const handlePop = (e) => {
      // 화면 위 오버레이(사진 뷰어 등)만 닫힌 경우는 화면 유지
      if (e.state?.lifelogScreen) return;
      if (!screenRef.current) return;
      const shouldRefresh = pendingRefreshRef.current
        ?? ['addRecord', 'editRecord'].includes(screenRef.current);
      pendingRefreshRef.current = null;
      setScreen(null);
      setScreenData(null);
      if (shouldRefresh) setRefreshSignal((s) => s + 1);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const navigate = (screenName, data = null) => {
    if (window.history.state?.lifelogScreen) {
      window.history.replaceState({ lifelogScreen: screenName }, '');
    } else {
      window.history.pushState({ lifelogScreen: screenName }, '');
    }
    setScreen(screenName);
    setScreenData(data);
  };

  const goBack = (shouldRefresh = false) => {
    if (window.history.state?.lifelogScreen) {
      pendingRefreshRef.current = shouldRefresh;
      window.history.back();
    } else {
      closeScreen(shouldRefresh);
    }
  };

  if (showOnboarding) {
    return (
      <OnboardingPage onDone={() => {
        localStorage.setItem(`lifelog_onboarding_done_${user.id}`, 'true');
        setShowOnboarding(false);
      }} />
    );
  }

  if (isLocked) {
    return <LockScreen onUnlock={() => setIsLocked(false)} />;
  }

  if (screen === 'addRecord') {
    return (
      <AddRecordPage
        user={user}
        currentProfile={currentProfile}
        profiles={profiles}
        onBack={() => goBack(true)}
      />
    );
  }

  if (screen === 'categoryManage') {
    return <CategoryManagePage user={user} onBack={() => goBack(false)} />;
  }

  if (screen === 'editRecord') {
    return (
      <AddRecordPage
        user={user}
        record={screenData}
        currentProfile={currentProfile}
        profiles={profiles}
        onBack={() => goBack(true)}
        onDeleted={() => goBack(true)}
      />
    );
  }

  if (screen === 'export') {
    return (
      <ExportPage
        user={user}
        currentProfile={currentProfile}
        onBack={() => goBack(false)}
      />
    );
  }

  if (screen === 'stats') {
    return (
      <StatsPage
        user={user}
        currentProfile={currentProfile}
        onBack={() => goBack(false)}
      />
    );
  }

  if (screen === 'profileManage') {
    return (
      <ProfileManagePage
        user={user}
        profiles={profiles}
        currentProfile={currentProfile}
        onSelectProfile={handleProfileChange}
        onBack={() => goBack(false)}
        onRefresh={initProfiles}
      />
    );
  }

  const tabProps = {
    user,
    onNavigate: navigate,
    refreshSignal,
    currentProfile,
  };

  const tabPages = {
    home: <HomePage {...tabProps} />,
    category: <CategoryPage {...tabProps} />,
    map: <MapPage {...tabProps} />,
    memories: <MemoriesPage {...tabProps} />,
    settings: <SettingsPage {...tabProps} />,
  };

  return (
    <div className="main-page">
      <div className="main-content">
        {tabPages[activeTab]}
      </div>
      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
}
